import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { Toaster } from '@/components/ui/sonner';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import Layout from '@/components/Layout';
import LoginPage from '@/pages/LoginPage';
import DashboardPage from '@/pages/DashboardPage';
import SubmissionsListPage from '@/pages/SubmissionsListPage';
import SubmissionDetailPage from '@/pages/SubmissionDetailPage';
import CreateSubmissionPage from '@/pages/CreateSubmissionPage';
import EditSubmissionPage from '@/pages/EditSubmissionPage';
import ApprovalsPage from '@/pages/ApprovalsPage';
import AgencyReviewPage from '@/pages/AgencyReviewPage';
import PostsPage from '@/pages/PostsPage';
import AdminPage from '@/pages/AdminPage';
import UsersPage from '@/pages/UsersPage';
import SettingsPage from '@/pages/SettingsPage';
import ProfilePage from '@/pages/ProfilePage';
import '@/App.css';

const AppRoutes: React.FC = () => {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage />} />
      
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Layout>
              <DashboardPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/submissions"
        element={
          <ProtectedRoute requiredRoles={['Marketing Executive', 'Marketing Head', 'Admin', 'Creative Agency', 'ReadOnly']}>
            <Layout>
              <SubmissionsListPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/submissions/new"
        element={
          <ProtectedRoute requiredRoles={['Marketing Executive', 'Creative Agency', 'Admin']}>
            <Layout>
              <CreateSubmissionPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/submissions/:id"
        element={
          <ProtectedRoute requiredRoles={['Marketing Executive', 'Marketing Head', 'Admin', 'Creative Agency', 'ReadOnly']}>
            <Layout>
              <SubmissionDetailPage />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/submissions/:id/edit"
        element={
          <ProtectedRoute requiredRoles={['Marketing Executive', 'Creative Agency', 'Admin']}>
            <Layout>
              <EditSubmissionPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/agency-review"
        element={
          <ProtectedRoute requiredRoles={['Creative Agency', 'Admin']}>
            <Layout>
              <AgencyReviewPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/approvals"
        element={
          <ProtectedRoute requiredRoles={['Marketing Head', 'Admin']}>
            <Layout>
              <ApprovalsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/posts"
        element={
          <ProtectedRoute requiredRoles={['Marketing Executive', 'Marketing Head', 'Admin', 'Creative Agency', 'ReadOnly']}>
            <Layout>
              <PostsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/admin"
        element={
          <ProtectedRoute requiredRoles={['Admin']}>
            <Layout>
              <AdminPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute requiredRoles={['Admin']}>
            <Layout>
              <UsersPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/admin/settings"
        element={
          <ProtectedRoute requiredRoles={['Admin']}>
            <Layout>
              <SettingsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Layout>
              <ProfilePage />
            </Layout>
          </ProtectedRoute>
        }
      />
      
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;